import React, { useEffect } from 'react';
import ChannelSettingsModal from '../ChannelSettingsModal';
import './ModalManager.css';

const KICK_DURATIONS = [
  { label: '5 min', minutes: 5 },
  { label: '30 min', minutes: 30 },
  { label: '1 hr', minutes: 60 },
  { label: '24 hr', minutes: 1440 },
  { label: 'Permanent', minutes: null },
];

const NOTIFICATION_MUTE_OPTIONS = [
  { label: '30 minutes', minutes: 30 },
  { label: '2 hours', minutes: 120 },
  { label: '6 hours', minutes: 360 },
  { label: 'Forever', minutes: null },
];

const VOICE_MODERATION_DURATIONS = [
  { label: '5 min', minutes: 5 },
  { label: '30 min', minutes: 30 },
  { label: '1 hr', minutes: 60 },
  { label: '24 hr', minutes: 1440 },
  { label: 'Forever', minutes: null },
];

function isRestrictionActive(until) {
  return Boolean(until && new Date(until) > new Date());
}

export default function ModalManager({
  // Channel settings
  showChannelSettings,
  channelSettingsChat,
  onCloseChannelSettings,
  onChannelIconUpdated,
  // Create child chat
  showCreateChildModal,
  newChildChatName,
  creatingChildChat,
  createChildChatError,
  selectedChatAccessRoot,
  onNewChildChatName,
  onCreateChildChat,
  onCloseCreateChildModal,
  // DM mute
  showDmNotificationMuteModal,
  selectedFriend,
  onMuteDm,
  onCloseDmMuteModal,
  // Kick
  kickTarget,
  onKick,
  onCancelKick,
  // Voice moderation
  voiceModerationTarget,
  voiceModerationBusy,
  voiceModerationError,
  onVoiceModeration,
  onCancelVoiceModeration,
}) {
  // Esc key: close topmost modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (voiceModerationTarget && !voiceModerationBusy) { onCancelVoiceModeration(); return; }
      if (kickTarget) { onCancelKick(); return; }
      if (showCreateChildModal && !creatingChildChat) { onCloseCreateChildModal(); return; }
      if (showDmNotificationMuteModal) { onCloseDmMuteModal(); return; }
      if (showChannelSettings) { onCloseChannelSettings(); return; }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    voiceModerationTarget, voiceModerationBusy,
    kickTarget,
    showCreateChildModal, creatingChildChat,
    showDmNotificationMuteModal,
    onCancelVoiceModeration, onCancelKick, onCloseCreateChildModal, onCloseDmMuteModal,
    showChannelSettings, onCloseChannelSettings,
  ]);

  return (
    <>
      {/* Channel Settings Modal */}
      {showChannelSettings && channelSettingsChat && (
        <ChannelSettingsModal
          chat={channelSettingsChat}
          onClose={onCloseChannelSettings}
          onIconUpdated={onChannelIconUpdated}
        />
      )}

      {/* Create Sub-Chat Modal */}
      {showCreateChildModal && selectedChatAccessRoot && (
        <div
          className="room-modal-backdrop"
          onClick={() => !creatingChildChat && onCloseCreateChildModal()}
        >
          <div
            className="room-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-create-child-title"
            onClick={e => e.stopPropagation()}
          >
            <h3 id="modal-create-child-title" className="room-modal-title">Create Sub-Chat</h3>
            <p className="room-modal-subtitle">What would you like to call it?</p>
            <input
              className="dash-input"
              value={newChildChatName}
              onChange={(e) => onNewChildChatName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onCreateChildChat()}
              placeholder="Sub-chat name"
              maxLength={64}
              autoFocus
            />
            {createChildChatError && (
              <p className="members-invite-status err">{createChildChatError}</p>
            )}
            <div className="room-modal-actions">
              <button
                className="dash-btn dash-btn-secondary"
                onClick={onCloseCreateChildModal}
                disabled={creatingChildChat}
              >
                Cancel
              </button>
              <button
                className="dash-btn"
                onClick={onCreateChildChat}
                disabled={creatingChildChat || !newChildChatName.trim()}
              >
                {creatingChildChat ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DM Notification Mute Modal */}
      {showDmNotificationMuteModal && selectedFriend && (
        <div className="room-modal-backdrop" onClick={onCloseDmMuteModal}>
          <div
            className="room-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-dm-mute-title"
            onClick={e => e.stopPropagation()}
          >
            <h3 id="modal-dm-mute-title" className="room-modal-title">Mute notifications</h3>
            <p className="room-modal-subtitle">For how long?</p>
            <div className="room-option-grid">
              {NOTIFICATION_MUTE_OPTIONS.map(option => (
                <button
                  key={option.label}
                  className={`dash-btn${option.minutes === null ? ' dash-btn-danger' : ' dash-btn-secondary'}`}
                  onClick={() => onMuteDm(option.minutes)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              className="dash-btn dash-btn-secondary room-modal-cancel"
              onClick={onCloseDmMuteModal}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Kick / Ban Modal */}
      {kickTarget && (
        <div className="room-modal-backdrop" onClick={onCancelKick}>
          <div
            className="room-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-kick-title"
            onClick={e => e.stopPropagation()}
          >
            <h3 id="modal-kick-title" className="room-modal-title">Kick / Ban Member</h3>
            <p className="room-modal-subtitle">Choose duration:</p>
            <div className="room-kick-options">
              {KICK_DURATIONS.map(d => (
                <button
                  key={d.label}
                  className={`dash-btn${d.minutes === null ? ' dash-btn-danger' : ' dash-btn-secondary'}`}
                  onClick={() => onKick(kickTarget, d.minutes)}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <button
              className="dash-btn dash-btn-secondary"
              style={{ marginTop: 8 }}
              onClick={onCancelKick}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Voice Moderation Modal */}
      {voiceModerationTarget && (
        <div
          className="room-modal-backdrop"
          onClick={() => !voiceModerationBusy && onCancelVoiceModeration()}
        >
          <div
            className="room-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-voice-mod-title"
            onClick={e => e.stopPropagation()}
          >
            <h3 id="modal-voice-mod-title" className="room-modal-title">Voice Moderation</h3>
            <p className="room-modal-subtitle">
              Manage voice access for{' '}
              {voiceModerationTarget.profiles?.display_name || voiceModerationTarget.profiles?.username || 'this member'}.
            </p>
            {voiceModerationError && (
              <p className="members-invite-status err">{voiceModerationError}</p>
            )}

            <div className="room-kick-options">
              {isRestrictionActive(voiceModerationTarget.voice_muted_until) ? (
                <button
                  className="dash-btn dash-btn-secondary"
                  disabled={voiceModerationBusy}
                  onClick={() => onVoiceModeration('unmute-member', voiceModerationTarget.user_id)}
                >
                  {voiceModerationBusy ? 'Saving…' : 'Unmute Voice'}
                </button>
              ) : (
                VOICE_MODERATION_DURATIONS.map(option => (
                  <button
                    key={`mute-${option.label}`}
                    className="dash-btn dash-btn-secondary"
                    disabled={voiceModerationBusy}
                    onClick={() => onVoiceModeration('mute-member', voiceModerationTarget.user_id, option.minutes)}
                  >
                    Mute {option.label}
                  </button>
                ))
              )}
            </div>

            <div className="room-kick-options">
              {isRestrictionActive(voiceModerationTarget.voice_kicked_until) ? (
                <button
                  className="dash-btn dash-btn-secondary"
                  disabled={voiceModerationBusy}
                  onClick={() => onVoiceModeration('allow-member', voiceModerationTarget.user_id)}
                >
                  {voiceModerationBusy ? 'Saving…' : 'Allow Back Into Voice'}
                </button>
              ) : (
                VOICE_MODERATION_DURATIONS.map(option => (
                  <button
                    key={`remove-${option.label}`}
                    className={`dash-btn${option.minutes === null ? ' dash-btn-danger' : ' dash-btn-secondary'}`}
                    disabled={voiceModerationBusy}
                    onClick={() => onVoiceModeration('remove-member', voiceModerationTarget.user_id, option.minutes)}
                  >
                    Remove {option.label}
                  </button>
                ))
              )}
            </div>

            <button
              className={`dash-btn ${voiceModerationTarget.voice_banned ? 'dash-btn-secondary' : 'dash-btn-danger'}`}
              disabled={voiceModerationBusy}
              onClick={() => onVoiceModeration(
                voiceModerationTarget.voice_banned ? 'unban-member' : 'ban-member',
                voiceModerationTarget.user_id,
              )}
            >
              {voiceModerationBusy
                ? 'Saving…'
                : voiceModerationTarget.voice_banned
                  ? 'Restore Voice Access'
                  : 'Ban From Voice'}
            </button>

            <button
              className="dash-btn dash-btn-secondary"
              style={{ marginTop: 8 }}
              onClick={onCancelVoiceModeration}
              disabled={voiceModerationBusy}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
